import React, { useMemo } from 'react';
import styled from 'styled-components';
import { OrderStatus, OrderDeliveryType, OrderProductStatus, ProductType } from '../../../../XV2';
import { UiGrid, UiBox, UiImage } from '@open-stack/ui';
import { XCard } from '../../../x/card/XCard';
import { useApiQuery } from '@orderx/lab/xr/http';
import { apiMeOrderUpdatePost } from '../../../../api/me.order-update.[post]';
import { RootIdPage } from '../../../../pages/id/RootIdPage';
import { useCustomerOrders } from '../../../../hooks/customer-orders';
import { useRouter } from '../../../../hooks/router';
import { XTranslate } from '../../../x/translate/XTranslate';
import groupBy from 'lodash/groupBy';
import { OrderViewCompactProductList } from './product-list/OrderViewCompactProductList';
import { XDrawerBottom } from '../../../x/drawer-bottom/XDrawerBottom';
import { XDrawerBottomHeader } from '../../../x/drawer-bottom/header/XDrawerBottomHeader';
import { useToggle } from '../../../../hooks/toggle';
import { XButtonBottom } from '../../../x/button/bottom/XButtonBottom';
import { OrderStatusAnimation } from '../../status/animation/OrderStatusAnimation';
import clsx from 'clsx';
import { OrderViewDefaultContact } from './contact/OrderViewDefaultContact';
import { XGridChain } from '../../../x/grid/chain/XGridChain';
import { OrderReceiptOrderProducts } from '../../receipt/order-products/OrderReceiptOrderProducts';
import { XButton, XText } from '@orderx/lab/ui';
import { OrderIdPageResult } from '../../../../pages/order/id/OrderIdPage';

export enum OrderViewDefaultStatus {
    PAYMENT_REQUIRED = 'PAYMENT_REQUIRED',
    PLACED = 'PLACED',
    IN_PREPARATION = 'IN_PREPARATION',
    READY = 'READY',
    IN_DELIVERY = 'IN_DELIVERY',
    DELIVERED = 'DELIVERED',
    CANCELLED = 'CANCELLED',
}

const Wrapper = styled(UiBox)`
    .center {
        text-align: center;
    }

    .close-button {
        position: fixed;
        top: calc(3 * var(--ui-spacing));
        right: calc(3 * var(--ui-spacing));
        z-index: var(--z-index-button-bottom);
    }

    .logo-container {
        border-radius: 100%;
        height: 88px;
        width: 88px;
        position: relative;
        margin: 0 auto;
        overflow: hidden;
    }

    .header {
        line-height: 1;

        td:first-child {
            padding-left: 0;
        }

        td:last-child {
            padding-right: 0;
        }
    }

    .sequence {
        font-size: var(--x-font-size-9);
    }

    &.${OrderViewDefaultStatus.READY} {
        .cards-container > * {
            background-color: var(--x-color-success);
        }
    }

    &.${OrderViewDefaultStatus.READY}:not(.${OrderDeliveryType.DELIVERY}) {
        .header {
            //min-height: calc(100% - 4 * (var(--ui-spacing)) - var(--bottom-nav-container-height));
            display: flex;
            flex-direction: column;

            * {
                color: white;
            }

            > :nth-child(2) {
                flex: 1;
                align-items: center;
            }

            .sequence {
                line-height: 1;

                .nr {
                    display: block;
                    font-size: 10rem;
                }
            }
        }
    }
`;

type Input = {
    order: OrderIdPageResult;
    linkBack?: string;
};

export function OrderViewDefault({ order, linkBack }: Input) {
    const { id: orderId } = order;
    const logo = order.outlet?.location.imageLogo;
    const customerOrders = useCustomerOrders();
    const { push } = useRouter();
    const isPickup = order.deliveryType === OrderDeliveryType.PICKUP;
    const hasTicket = Boolean(order.products?.find((product) => product.type === ProductType.TICKET));

    const [isProductsVisible, toggleIsProductsVisible] = useToggle();

    const orderState = useMemo(() => {
        const numTotalProducts = order.orderProducts.length;
        const orderProductsByStatus = groupBy(order.orderProducts, (item) => item.status);
        const orderProductsNotCancelled = order.orderProducts.filter(
            ({ status }) => status !== OrderProductStatus.CANCELLED,
        );
        const statusOfAllProducts =
            numTotalProducts > 0 &&
            orderProductsNotCancelled.filter(({ status }) => status === orderProductsNotCancelled[0]?.status).length ===
                orderProductsNotCancelled.length &&
            orderProductsNotCancelled[0]?.status;
        const isPaymentRequired = order.status === OrderStatus.PAYMENT_REQUIRED;
        const isDelivered = statusOfAllProducts === OrderProductStatus.DELIVERED;
        const isReady = orderProductsByStatus[OrderProductStatus.READY]?.length > 0;
        const inPreparation = !isReady && orderProductsByStatus[OrderProductStatus.IN_PREPARATION]?.length > 0;
        const isCancelled =
            order.status === OrderStatus.CANCELLED ||
            numTotalProducts === orderProductsByStatus[OrderProductStatus.CANCELLED]?.length;
        const orderProductsReady = orderProductsByStatus[OrderProductStatus.READY] || [];

        if (isCancelled) {
            return {
                title: <XTranslate id="order_page.cancelled" />,
                status: OrderViewDefaultStatus.CANCELLED,
                isContactVisible: false,
                orderProductsReady,
                isDelivered,
                isCancelled,
            };
        } else if (isPaymentRequired) {
            return {
                title: <XTranslate id="order_page.payment_required" />,
                status: OrderViewDefaultStatus.PAYMENT_REQUIRED,
                isContactVisible: false,
                orderProductsReady,
                isDelivered,
                isCancelled,
            };
        } else if (isDelivered) {
            return {
                title: <XTranslate id="order_page.delivered" />,
                status: OrderViewDefaultStatus.DELIVERED,
                isContactVisible: false,
                orderProductsReady,
                isDelivered,
                isCancelled,
            };
        } else if (isReady) {
            return {
                title: <XTranslate id={`order_page.${order.deliveryType}.ready`} />,
                status: OrderViewDefaultStatus.READY,
                isContactVisible: false,
                orderProductsReady,
                isDelivered,
                isCancelled,
            };
        } else if (inPreparation) {
            return {
                title: <XTranslate id="order_page.in_preparation" />,
                status: OrderViewDefaultStatus.IN_PREPARATION,
                isContactVisible: true,
                orderProductsReady,
                isDelivered,
                isCancelled,
            };
        } else {
            return {
                title: <XTranslate id="order_page.placed" />,
                status: OrderViewDefaultStatus.PLACED,
                isContactVisible: true,
                orderProductsReady,
                isDelivered,
                isCancelled,
            };
        }
    }, [order, isPickup]);

    const { isDelivered } = orderState;

    const apiQueryCloseButton = useApiQuery(async (params) => {
        const forceDismiss = params?.forceDismiss;

        if (order.isDisplayedToCustomer && (order.status === OrderStatus.ARCHIVED || isDelivered || forceDismiss)) {
            await apiMeOrderUpdatePost({
                body: {
                    action: 'DISMISS',
                    orderId,
                },
            });

            await customerOrders.loadOrders();
        }

        if (linkBack) {
            push(linkBack);
        } else {
            push(RootIdPage.route, { id: order.zone.hash });
        }
    });

    const isContactVisible = order.deliveryType === OrderDeliveryType.PICKUP && orderState.isContactVisible;
    const inAnyReadyState = [OrderViewDefaultStatus.READY, OrderViewDefaultStatus.DELIVERED].includes(
        orderState.status,
    );

    const closeButton = (
        <XButton
            isLoading={apiQueryCloseButton.isLoading}
            onClick={apiQueryCloseButton.run}
            icon="close"
            className="close-button"
            variant={'outlined'}
        />
    );

    if (hasTicket) {
        return (
            <Wrapper>
                {closeButton}

                <XGridChain className="cards-container">
                    <XCard gap={4} padding={4} className="header">
                        {logo && (
                            <div className="logo-container">
                                <UiImage src={logo} type="cover" />
                            </div>
                        )}

                        <UiGrid gap={0}>
                            <XText color="text-secondary" className={'center'}>
                                {order.outlet?.name || '-'} • {order.zone?.name || '-'}
                            </XText>
                            <XText className="sequence center">
                                <span className="title">
                                    <XTranslate id="order_page.sequence_nr" />
                                </span>

                                <XText className="nr">{order.sequence}</XText>
                            </XText>
                        </UiGrid>
                    </XCard>
                </XGridChain>
            </Wrapper>
        );
    }

    return (
        <>
            <Wrapper padding={2} className={clsx(orderState.status, order.deliveryType)}>
                {closeButton}

                <XGridChain className="cards-container">
                    <XCard gap={4} padding={4} className="header">
                        {logo && (
                            <div className="logo-container">
                                <UiImage src={logo} type="cover" />
                            </div>
                        )}

                        <UiGrid gap={0}>
                            <XText color="text-secondary" className={'center'}>
                                {order.outlet?.name || '-'} • {order.zone?.name || '-'}
                            </XText>

                            <XText className="sequence center">
                                <span className="title">
                                    <XTranslate id="order_page.sequence_nr" />
                                </span>

                                <XText className="nr">{order.sequence}</XText>
                            </XText>
                        </UiGrid>
                    </XCard>

                    {!orderState.isCancelled &&
                        (orderState.status !== OrderViewDefaultStatus.READY ||
                            (orderState.status === OrderViewDefaultStatus.READY && !isPickup)) && (
                            <OrderStatusAnimation orderState={orderState} />
                        )}

                    {orderState.status === OrderViewDefaultStatus.READY && (
                        <>
                            {isPickup && (
                                <XCard padding={2}>
                                    <XText className={'title'}>{orderState.title}</XText>
                                </XCard>
                            )}

                            <XCard padding={2}>
                                <OrderViewCompactProductList orderProducts={orderState.orderProductsReady} />
                            </XCard>
                        </>
                    )}

                    {orderState.status === OrderViewDefaultStatus.CANCELLED && (
                        <XCard onClick={() => apiQueryCloseButton.run({ forceDismiss: true })} backgroundColor="error">
                            <UiGrid padding={2}>
                                <XText className={'title'}>{orderState.title}</XText>

                                <XButton className="confirm-button" isLoading={apiQueryCloseButton.isLoading}>
                                    Okay...
                                </XButton>
                            </UiGrid>
                        </XCard>
                    )}

                    {isContactVisible && <OrderViewDefaultContact order={order} />}

                    {!inAnyReadyState && (
                        <XCard padding={2}>
                            <XText>NB! You can also order from other places...</XText>
                        </XCard>
                    )}
                </XGridChain>
            </Wrapper>

            <XButtonBottom onClick={toggleIsProductsVisible} color="surface" hasPlaceholder={false}>
                <XTranslate id="order_page.button_details" />
            </XButtonBottom>

            {isProductsVisible && (
                <XDrawerBottom onClose={toggleIsProductsVisible}>
                    <XDrawerBottomHeader
                        title={<XTranslate id="order_page.drawer_details.title" />}
                        onClose={toggleIsProductsVisible}
                    />

                    <Wrapper>
                        <OrderReceiptOrderProducts orderId={orderId} />
                    </Wrapper>
                </XDrawerBottom>
            )}
        </>
    );
}
